import { Column } from '@tanstack/react-table';
import { ArrowsIcon, CheckedSquareIcon, CloseIcon, UncheckedSquareIcon } from '@va/icons';
import { useTranslate } from '@va/localization';
import {
  Button,
  fontWeights,
  MenuList,
  MenuListItemType,
  paragraphSizes,
  ParagraphWithTooltip,
} from '@va/ui/design-system';
import { useTooltipContext } from '@va/ui/tooltips';
import { RefObject, useCallback, useMemo } from 'react';
import { ColumnVisibilityColumn, useColumnVisibilityContext } from './ColumnVisibilityContext';

function useColumns(columnsRef: RefObject<Column<any, unknown>[]>): ColumnVisibilityColumn[] {
  const translate = useTranslate();

  return columnsRef.current.map((col, index) => {
    const label = col.getLabel();

    return {
      id: col.id,
      label: label ?? translate(`all.table.columnLabels.${col.id}`),
      canHide: index !== 0,
    };
  });
}

export const ColumnVisibilityMenuContent = () => {
  const translate = useTranslate();
  const tooltip = useTooltipContext();
  const { columnsRef, columnVisibility, onColumnVisibilityChange } = useColumnVisibilityContext();

  const columns = useColumns(columnsRef);

  const toggleColumn = useCallback(
    (columnId: string) => {
      const isCurrentlyVisible = columnVisibility[columnId] !== false;
      onColumnVisibilityChange((prev) => ({
        ...prev,
        [columnId]: !isCurrentlyVisible,
      }));
    },
    [columnVisibility, onColumnVisibilityChange],
  );

  const menuItems = useMemo<MenuListItemType[]>(
    () =>
      columns.map((col) => {
        const isVisible = columnVisibility[col.id] !== false;

        return {
          id: col.id,
          label: col.label,
          icon: <ArrowsIcon />,
          selected: isVisible,
          endAdornment: isVisible ? (
            <CheckedSquareIcon color='var(--color-primary)' className='w-[1.15rem] h-[1.15rem]' />
          ) : (
            <UncheckedSquareIcon className='w-[1.15rem] h-[1.15rem]' />
          ),
          disabled: !col.canHide,
          onClick: () => toggleColumn(col.id),
        };
      }),
    [columnVisibility, columns, toggleColumn],
  );

  return (
    <div className='bg-white p-2.5 min-w-72'>
      <div className='flex justify-between items-center mb-2'>
        <ParagraphWithTooltip weight={fontWeights.semibold} size={paragraphSizes.tiny}>
          {translate('tooltip.label.showHideTableColumns')}
        </ParagraphWithTooltip>
        <Button
          size='small'
          color='quaternary'
          icon={() => <CloseIcon color='#3C3C3C' />}
          onClick={() => tooltip.context.onOpenChange(false)}
        />
      </div>
      <MenuList className='max-h-[22rem]' items={menuItems} />
    </div>
  );
};
